// BDSJEA-371 (Historial, incremento 2): saneado PURO del `motivo` que muestran,
// buscan y ordenan las tablas del historial, por LISTA BLANCA. Defensa en
// profundidad: el backend ya no deberia mandar nombres, host/URL ni CUIT, pero
// si llega texto crudo (backend viejo, stub, CSV desplazado) la pantalla nunca
// lo muestra: del texto libre NO se conserva nada salvo tokens estructurados.
//
// Reglas (en orden):
//  1. vacio => "" (la tabla muestra "-").
//  2. "Fallo HTTP ..." => "Fallo HTTP" (+ el codigo de estado si lo trae).
//  3. Ya saneado (MOTIVO_OCULTO, una etiqueta o "<etiqueta> (tokens)") => se
//     vuelve a armar con los mismos tokens (idempotencia).
//  4. Si claveMotivo lo reconoce => SOLO etiquetaClave(clave) (tabla cerrada),
//     mas entre parentesis los tokens estructurados seguros del texto:
//     "entidad <codigo conocido o numero>" e "id_cliente|id_plan|id_entidad|
//     id_productor <numero>". Nada mas del texto libre.
//  5. Cualquier otra cosa => "Otro motivo (ver log)".
import {
  CLAVE_OTRO,
  CLAVES_POR_CONTEXTO,
  CONTEXTOS_ACTIVIDADES,
  claveMotivo,
  etiquetaClave,
} from "./categorizacionHistorial"

export const MOTIVO_OCULTO = "Otro motivo (ver log)"
// BDSJEA-441: las actividades traen claves cerradas del backend (sin log CSV al
// que remitir): el motivo desconocido se rotula sin «(ver log)».
export const MOTIVO_OCULTO_ACTIVIDADES = "Otro motivo"
const ocultoPara = contexto =>
  CONTEXTOS_ACTIVIDADES.includes(contexto)
    ? MOTIVO_OCULTO_ACTIVIDADES
    : MOTIVO_OCULTO

// Siglas de entidad conocidas (catalogo del banco). Un codigo fuera de la lista
// (o del que se pase por parametro) NO se muestra: podria ser un apellido.
export const CODIGOS_ENTIDAD_CONOCIDOS = Object.freeze(["BER", "BSJ", "BSF"])
const MAX_TOKENS = 4

const FALLO_HTTP = /^fallo\s+http\b\s*(?:[:-]\s*)?(\d{3})?(?!\d)/i

// Etiquetas de las claves cerradas: el resultado de una pasada previa.
const ETIQUETAS = new Set(
  Object.values(CLAVES_POR_CONTEXTO)
    .flat()
    .filter(clave => clave !== CLAVE_OTRO)
    .map(etiquetaClave),
)

const TOKEN_ENTIDAD = /(?<![\w])entidad\s+([A-Za-z0-9]{1,6})(?![\w])/gi
const TOKEN_ID =
  /(?<![\w])(id_cliente|id_plan|id_entidad|id_productor)\s+(\d{1,8})(?![\w])/gi

// Tokens estructurados seguros del texto, en orden de aparicion y sin repetir.
const tokensSeguros = (texto, codigos) => {
  const permitidos = new Set(codigos.map(c => String(c).trim().toUpperCase()))
  const hallados = []
  const agregar = (indice, token) => {
    if (!hallados.some(h => h.token === token)) hallados.push({ indice, token })
  }
  for (const m of texto.matchAll(TOKEN_ENTIDAD)) {
    const valor = m[1]
    if (/^\d+$/.test(valor)) agregar(m.index, `entidad ${valor}`)
    else if (valor === valor.toUpperCase() && permitidos.has(valor))
      agregar(m.index, `entidad ${valor}`)
  }
  for (const m of texto.matchAll(TOKEN_ID)) {
    agregar(m.index, `${m[1].toLowerCase()} ${m[2]}`)
  }
  return hallados
    .sort((a, b) => a.indice - b.indice)
    .slice(0, MAX_TOKENS)
    .map(h => h.token)
}

const conTokens = (etiqueta, tokens) =>
  tokens.length ? `${etiqueta} (${tokens.join(", ")})` : etiqueta

// "<etiqueta>" o "<etiqueta> (...)" de una pasada previa => etiqueta, o null.
// De mayor a menor longitud: "Ya existía (saltado) (id_cliente 3)" es la
// etiqueta "Ya existía (saltado)" y no "Ya existía".
const ETIQUETAS_POR_LARGO = [...ETIQUETAS].sort((a, b) => b.length - a.length)
const etiquetaPrevia = bruto => {
  if (ETIQUETAS.has(bruto)) return bruto
  for (const etiqueta of ETIQUETAS_POR_LARGO) {
    if (
      bruto.startsWith(`${etiqueta} (`) &&
      bruto.endsWith(")") &&
      bruto.length > etiqueta.length + 3
    ) {
      return etiqueta
    }
  }
  return null
}

/**
 * Texto seguro de un `motivo` para mostrar, buscar y ordenar en las tablas.
 * @param {*} motivo texto crudo (cualquier valor; no string => "")
 * @param {"productores"|"establecimientos"|"arca"} [contexto]
 * @param {string[]} [codigosEntidad] siglas de entidad que se pueden mostrar
 *   (ademas de CODIGOS_ENTIDAD_CONOCIDOS), p. ej. las del catalogo
 * @returns {string} "" si esta vacio; si no, una etiqueta cerrada (con tokens
 *   estructurados opcionales) o MOTIVO_OCULTO; nunca texto libre del motivo
 */
export function motivoVisible(motivo, contexto, codigosEntidad = []) {
  if (typeof motivo !== "string") return ""
  const bruto = motivo.trim()
  if (!bruto) return ""
  const codigos = [
    ...CODIGOS_ENTIDAD_CONOCIDOS,
    ...(Array.isArray(codigosEntidad) ? codigosEntidad : []),
  ]

  const http = FALLO_HTTP.exec(bruto)
  if (http) return http[1] ? `Fallo HTTP ${http[1]}` : "Fallo HTTP"
  if (bruto === MOTIVO_OCULTO) return ocultoPara(contexto)

  const previa = etiquetaPrevia(bruto)
  if (previa) {
    const resto = bruto.slice(previa.length)
    return conTokens(previa, tokensSeguros(resto, codigos))
  }

  const clave = claveMotivo(bruto, contexto)
  if (clave === CLAVE_OTRO) return ocultoPara(contexto)
  return conTokens(etiquetaClave(clave), tokensSeguros(bruto, codigos))
}
